import { useCallback, useEffect, useState } from "react";
import { Camera, ChevronDown, ChevronRight, Clock, Edit, Eye, EyeOff, FileText, History, Mic, Paperclip, Printer, RotateCcw, Tag, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/v5/components/ui/dialog";
import { Button } from "@/v5/components/ui/button";
import { Separator } from "@/v5/components/ui/separator";
import { Spinner } from "@/v5/components/ui/spinner";
import type { PatientNoteDetail, NoteHistoryEntry } from "@/v5/services/notes.service";
import * as notesSvc from "@/v5/services/notes.service";
import { CameraCaptureModal } from "@/components/medical/CameraCaptureModal";
import { PrintOptionsDialog } from "@/v5/components/PrintOptionsDialog";
import { LabPdfPreviewPanel } from "@/pages/patients/labs/LabPdfPreviewPanel";
import { isPreviewableFilename } from "@/lib/previewableFiles";
import { toast } from "@/store/toastStore";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { pacificToWallClock, getTimezoneAbbreviation } from "@/lib/facilityTimezone";
import { useAuth } from "@/hooks/useAuth";
import { TaskModal } from "@/pages/TasksPage";
import type { PatientSummary } from "@/types/patient.types";

interface NoteViewModalProps {
  patientId: string;
  noteId: number;
  /** Prefills the patient on the Add Task modal - see NoteDetailPanel. */
  patientSummary?: PatientSummary | null;
  onClose: () => void;
  onEdit: (noteId: number) => void;
  /**
   * Called after a mutation (visibility toggle, delete, restore, attachment)
   * so the parent can refresh the list AND the underlying NoteDetailPanel,
   * which stays mounted behind this modal and would otherwise show stale state.
   */
  onMutated?: () => void;
}

export function NoteViewModal({ patientId, noteId, patientSummary, onClose, onEdit, onMutated }: NoteViewModalProps) {
  const userTz = useUserTimezone();
  const { user: currentUser } = useAuth();
  const [note, setNote] = useState<PatientNoteDetail | null>(null);
  const [history, setHistory] = useState<NoteHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(new Set());
  const [cameraOpen, setCameraOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  // Attachment being previewed. Replaces the note body inside this dialog
  // rather than opening a tab, so popup blockers can't swallow the click.
  const [previewAttachment, setPreviewAttachment] = useState<
    { attachmentId: number; filename: string } | null
  >(null);

  const fetchNote = useCallback(async () => {
    setLoading(true);
    try {
      const [noteData, histData] = await Promise.all([
        notesSvc.getNote(patientId, noteId),
        notesSvc.getNoteHistory(patientId, noteId),
      ]);
      setNote(noteData);
      setHistory(histData);
    } catch { toast.error("Failed to load note"); }
    finally { setLoading(false); }
  }, [patientId, noteId]);

  useEffect(() => { void fetchNote(); }, [fetchNote]);

  async function handleDelete() {
    if (!window.confirm("Delete this note?")) return;
    try { await notesSvc.deleteNote(patientId, noteId); toast.success("Note deleted"); onMutated?.(); onClose(); }
    catch { toast.error("Failed to delete note"); }
  }

  async function handleRestore() {
    if (!window.confirm("Restore this note?")) return;
    try { await notesSvc.restoreNote(patientId, noteId); toast.success("Note restored"); void fetchNote(); onMutated?.(); }
    catch { toast.error("Failed to restore note"); }
  }

  async function handleTogglePortal() {
    try {
      const result = await notesSvc.togglePortalVisibility(patientId, noteId);
      setNote((prev) => prev && { ...prev, visibleToPatient: result.visible, portalId: result.portalId });
      toast.success(result.visible ? "Note now visible to patient" : "Note hidden from patient");
      onMutated?.();
    } catch { toast.error("Failed to toggle visibility"); }
  }

  function fmt(d: string | null): string {
    if (!d) return "\u2014";
    try {
      const raw = d.replace(" ", "T");
      const pacDate = raw.slice(0, 10);
      const pacTime = raw.slice(11, 16) || "00:00";
      const wall = pacificToWallClock(pacDate, pacTime, userTz);
      const dt = new Date(`${wall.date}T${wall.time}:00Z`);
      if (Number.isNaN(dt.getTime())) return d;
      const formatted = dt.toLocaleString("en-US", {
        // `dt` is the resolved wall clock anchored to UTC, so formatting in UTC
        // prints those components; formatting in `userTz` would shift them again.
        timeZone: "UTC",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
      return `${formatted} ${getTimezoneAbbreviation(userTz, dt)}`;
    } catch { return d; }
  }

  function toggleHistoryItem(id: number) {
    setExpandedHistory((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  return (
    <>
    <Dialog open={!printOpen && !taskOpen} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-6xl w-[90vw] sm:max-w-none max-h-[85vh] flex flex-col overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> View Note</DialogTitle></DialogHeader>
        {previewAttachment ? (
          <div className="h-[70vh] min-h-0">
            <LabPdfPreviewPanel
              patientId={Number(patientId)}
              target={{
                kind: "attachment",
                patientId: Number(patientId),
                attachmentId: previewAttachment.attachmentId,
                title: previewAttachment.filename,
              }}
              onClose={() => setPreviewAttachment(null)}
              onNewTab={() => setPreviewAttachment(null)}
            />
          </div>
        ) : loading || !note ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center flex-wrap gap-2 pb-2 border-b">
              <Button variant="outline" size="sm" onClick={handleTogglePortal} className="gap-1">
                {note.visibleToPatient ? <><Eye className="h-3.5 w-3.5 text-green-600" /> Visible to Patient</> : <><EyeOff className="h-3.5 w-3.5" /> Hidden from Patient</>}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCameraOpen(true)} className="gap-1"><Camera className="h-3.5 w-3.5" /> Capture Photo</Button>
              <Button variant="outline" size="sm" className="gap-1"><Mic className="h-3.5 w-3.5" /> Add Recording</Button>
              <Button variant="outline" size="sm" onClick={() => setTaskOpen(true)} className="gap-1"><Tag className="h-3.5 w-3.5" /> Add Task</Button>
              <div className="flex-1" />
              {note.active === 1 ? (
                <Button variant="outline" size="sm" onClick={handleDelete} className="gap-1 text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleRestore} className="gap-1 text-green-600"><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setPrintOpen(true)} className="gap-1"><Printer className="h-3.5 w-3.5" /> Print</Button>
              {note.active === 1 && currentUser?.id === note.authorId && <Button size="sm" onClick={() => onEdit(noteId)} className="gap-1"><Edit className="h-3.5 w-3.5" /> Edit</Button>}
            </div>

            {/* See NoteDetailPanel: shown only when the deleter is known. */}
            {note.active !== 1 && note.deletedBy && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                Deleted by {note.deletedBy}
                {note.deletedAt ? ` · ${fmt(note.deletedAt)}` : ''}
              </div>
            )}

            {/* Fields */}
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm">
              <span className="font-medium text-muted-foreground text-right">Type:</span>
              <span>{note.typeName}</span>
              <span className="font-medium text-muted-foreground text-right">Date:</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-muted-foreground" />{fmt(note.noteDate)}</span>
              <span className="font-medium text-muted-foreground text-right">Subject:</span>
              <span className="font-medium">{note.title}</span>
              <span className="font-medium text-muted-foreground text-right">Note:</span>
              <div className="prose prose-sm max-w-none border rounded-md p-3 bg-muted/30 min-h-[100px]" dangerouslySetInnerHTML={{ __html: note.content }} />
              <span className="font-medium text-muted-foreground text-right">Attachments:</span>
              <div className="border rounded-md p-3 bg-muted/30">
                {note.attachments.length === 0 ? (
                  <span className="text-muted-foreground text-xs">No attachments</span>
                ) : (
                  <div className="space-y-1">
                    {note.attachments.map((a) => (
                      <button
                        key={a.attachmentId}
                        type="button"
                        onClick={() => {
                          // PDFs/images preview in place; anything the browser
                          // can't render (.docx, .label) downloads instead.
                          if (isPreviewableFilename(a.filename)) {
                            setPreviewAttachment({ attachmentId: a.attachmentId, filename: a.filename });
                            return;
                          }
                          void notesSvc.downloadAttachment(patientId, a.attachmentId, a.filename)
                            .catch((err) => toast.error(err instanceof Error ? err.message : "Failed to download attachment"));
                        }}
                        className="flex items-center gap-1.5 text-primary hover:underline text-xs"
                      >
                        <Paperclip className="h-3 w-3" />{a.filename}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* History */}
            <div>
              <button type="button" onClick={() => setHistoryOpen(!historyOpen)} className="flex items-center gap-2 text-sm font-semibold hover:text-primary">
                <History className="h-4 w-4" /> Note History ({history.length})
                {historyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {historyOpen && (
                <div className="mt-3 space-y-2 pl-6">
                  {history.length === 0 ? <p className="text-xs text-muted-foreground">No history available</p> : history.map((h) => (
                    <div key={h.historyId} className="border rounded-md overflow-hidden">
                      <button type="button" onClick={() => toggleHistoryItem(h.historyId)} className="w-full flex items-center justify-between px-3 py-2 text-xs bg-muted/50 hover:bg-muted">
                        <span><strong>{h.authorName || "System"}</strong> {"\u2014"} {fmt(h.dateCreated)} {h.typeName && ` \u2014 ${h.typeName}`}</span>
                        {expandedHistory.has(h.historyId) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                      {expandedHistory.has(h.historyId) && (
                        <div className="p-3 text-xs space-y-2">
                          <div><strong>Subject:</strong> {h.title}</div>
                          <div className="prose prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: h.content }} />
                          {h.attachmentsAdded.length > 0 && <div className="text-green-600"><strong>Files added:</strong> {h.attachmentsAdded.map((a) => a.filename).join(", ")}</div>}
                          {h.attachmentsRemoved.length > 0 && <div className="text-destructive"><strong>Files removed:</strong> {h.attachmentsRemoved.map((a) => a.filename).join(", ")}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      {/* Camera capture — v5-native replacement for the legacy
          /patient_photo_capture.php popup. Each photo uploads as a note
          attachment and the surrounding note refetches so the new file
          shows up immediately in the Attachments list. */}
      <CameraCaptureModal
        isOpen={cameraOpen}
        multi
        onCapture={(file) => {
          void notesSvc
            .uploadAttachment(patientId, noteId, file, { mirrorToPhotos: true })
            .then(() => {
              toast.success("Photo attached");
              void fetchNote();
              onMutated?.();
            })
            .catch(() => toast.error("Failed to attach photo"));
        }}
        onClose={() => setCameraOpen(false)}
      />

    </Dialog>

      {printOpen && (
        <PrintOptionsDialog
          patientId={patientId}
          noteIds={String(noteId)}
          mode="pdf"
          onClose={() => setPrintOpen(false)}
        />
      )}

      {/* See NoteDetailPanel - same button, same link. */}
      {taskOpen && (
        <TaskModal
          initialPatient={patientSummary ?? undefined}
          initialNoteId={noteId}
          onClose={() => setTaskOpen(false)}
          onSaved={() => setTaskOpen(false)}
        />
      )}
    </>
  );
}
